import type {
  ITicketConfigTour,
  TicketConfigTourPayload,
} from '@/shared/api/types/ticket-config-types'
import type { TourSelections } from './model'
import { useState } from 'react'
import { buildOverridePayload, buildSelections, hasUnsavedChanges, toggleFeature } from './model'

interface UseTicketConfigToursParams {
  tours: ITicketConfigTour[]
  defaultFeatureIds: string[]
}

interface UseTicketConfigToursResult {
  selections: TourSelections
  isDirty: boolean
  payload: TicketConfigTourPayload[]
  setFeatureChecked: (tourId: string, featureId: string, checked: boolean) => void
  resetTourToDefault: (tourId: string) => void
  resetAll: () => void
}

/** The card is remounted (via `key`) whenever the server data changes, so the initial state is enough. */
export function useTicketConfigTours({
  tours,
  defaultFeatureIds,
}: UseTicketConfigToursParams): UseTicketConfigToursResult {
  const [selections, setSelections] = useState<TourSelections>(() => buildSelections(tours))

  function setFeatureChecked(tourId: string, featureId: string, checked: boolean) {
    setSelections((current) => ({
      ...current,
      [tourId]: toggleFeature(current[tourId] ?? [], featureId, checked),
    }))
  }

  function resetTourToDefault(tourId: string) {
    setSelections((current) => ({ ...current, [tourId]: defaultFeatureIds }))
  }

  return {
    selections,
    isDirty: hasUnsavedChanges(tours, selections, defaultFeatureIds),
    payload: buildOverridePayload(tours, selections, defaultFeatureIds),
    setFeatureChecked,
    resetTourToDefault,
    resetAll: () => setSelections(buildSelections(tours)),
  }
}
